import { useState } from "react";
import { api, formatApiErrorDetail, uploadDocument } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, FileText, Loader2, Paperclip, Pencil, Plus, Truck, X } from "lucide-react";
import { PO_INVOICE_META, PO_PRIORITY_META, PO_RECEIVING_META, PO_SHIPMENT_META, PoBadge } from "@/lib/poStatus";

const num = (n) => Math.round(n).toLocaleString();
export const money = (v, cur = "USD") => (cur === "USD" ? "$" : `${cur} `) + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtShort = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");

export const patchMeta = async (po, fields, onChanged, okMsg) => {
  try {
    await api.patch(`/purchase-orders/${po.id}/meta`, { fields });
    if (okMsg) toast.success(okMsg);
    onChanged?.();
  } catch (e) {
    toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
  }
};

function InlineDate({ po, field, value, onChanged, testId, highlight = "" }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <input type="date" autoFocus defaultValue={value || ""} className="rounded-sm border border-slate-300 px-1 py-0.5 text-[11px]"
        onBlur={(e) => { setEditing(false); if (e.target.value !== value) patchMeta(po, { [field]: e.target.value }, onChanged); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditing(false); }}
        data-testid={`${testId}-input`} />
    );
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`cursor-pointer whitespace-nowrap text-xs hover:text-[#002FA7] hover:underline ${value ? highlight || "text-slate-700" : "text-slate-300"}`}
      data-testid={testId}>{value ? fmtShort(value) : "Set"}</button>
  );
}

export function PoDatesCell({ po, onChanged, todayIso }) {
  const pastDue = po.status !== "received" && po.status !== "cancelled" && po.rdd && po.rdd < todayIso;
  const rows = [
    ["PO", "order_date", ""],
    ["ESD", "esd", ""],
    ["RDD", "rdd", pastDue ? "font-semibold text-red-600" : ""],
    ["EDD", "edd", po.edd && po.rdd && po.edd > po.rdd ? "font-semibold text-amber-600" : ""],
    ["ADD", "actual_delivery_date", "text-emerald-700"],
  ];
  return (
    <div className="space-y-0.5 whitespace-nowrap" data-testid={`po-dates-${po.id}`} onClick={(e) => e.stopPropagation()}>
      {rows.map(([label, field, hl]) => (
        <div key={field} className="flex items-center gap-1.5">
          <span className="w-8 shrink-0 text-[9px] font-bold uppercase text-slate-400">{label}</span>
          <InlineDate po={po} field={field} value={po[field]} onChanged={onChanged} testId={`po-${field}-${po.id}`} highlight={hl} />
        </div>
      ))}
    </div>
  );
}

export function PoFilesCell({ po, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const files = po.po_docs || [];
  const upload = async (fs) => {
    if (!fs.length) return;
    setBusy(true);
    try {
      const arr = [...files];
      for (const f of fs) {
        const d = await uploadDocument(f);
        arr.push({ name: d.name || f.name, url: d.url, path: d.path });
      }
      await patchMeta(po, { po_docs: arr }, onChanged, `PO file attached to ${po.po_number}`);
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  const remove = (i) => patchMeta(po, { po_docs: files.filter((_, x) => x !== i) }, onChanged, "PO file removed");
  const fileInput = (
    <input type="file" className="hidden" multiple onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; upload(fs); }} />
  );
  const dropProps = {
    onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); },
    onDragEnter: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); },
    onDragLeave: (e) => { e.preventDefault(); setDragOver(false); },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation(); setDragOver(false);
      const fs = Array.from(e.dataTransfer?.files || []);
      if (fs.length) upload(fs);
    },
  };
  const SQ = "flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border transition-colors";
  if (!files.length) {
    return (
      <label onClick={(e) => e.stopPropagation()} {...dropProps} title="Upload or drag the PO document here"
        className={`${SQ} cursor-pointer ${dragOver ? "border-dashed border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-dashed border-slate-300 text-slate-400 hover:border-[#002FA7] hover:text-[#002FA7]"}`}
        data-testid={`po-file-upload-${po.id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
        {fileInput}
      </label>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} {...dropProps}
          title={`PO file${files.length > 1 ? `s — ${files.length}` : ""}`}
          className={`${SQ} relative ${dragOver ? "border-dashed border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-500"}`}
          data-testid={`po-files-${po.id}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
          {files.length > 1 && <span className="absolute -right-1 -top-1 rounded-full bg-emerald-600 px-1 text-[9px] font-bold leading-3 text-white">{files.length}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">PO Documents — {po.po_number}</p>
        {files.map((a, i) => (
          <div key={`${a.path}-${i}`} className="flex items-center justify-between gap-2 py-1 text-sm">
            <a href={a.url} target="_blank" rel="noreferrer" className="truncate text-[#002FA7] hover:underline" data-testid={`po-file-${po.id}-${i}`}>{a.name}</a>
            <button type="button" onClick={() => remove(i)} className="shrink-0 text-slate-400 hover:text-red-600" data-testid={`po-file-remove-${po.id}-${i}`} title="Remove"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <label {...dropProps}
          className={`mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-dashed px-2 py-1 text-xs font-semibold ${dragOver ? "border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-slate-300 text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]"}`}
          data-testid={`po-file-upload-more-${po.id}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {dragOver ? "Drop PO here" : "Upload or drag another"}
          {fileInput}
        </label>
      </PopoverContent>
    </Popover>
  );
}

export function PoOrderCell({ po }) {
  const lines = po.lines || [];
  const single = lines.length === 1;
  const l0 = lines[0];
  const shown = lines.slice(0, 3);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="group block text-left" onClick={(e) => e.stopPropagation()} title="View items in this order" data-testid={`po-order-cell-${po.id}`}>
          {single ? (
            <>
              <div className="font-medium text-slate-800 underline decoration-dotted decoration-slate-300 underline-offset-2 group-hover:text-[#002FA7]">{l0?.name}</div>
              <div className="text-xs text-slate-500">
                <span className="font-semibold tabular-nums text-slate-700">{num(l0?.quantity || 0)}</span> {l0?.unit}
                {l0?.sku ? ` · SKU ${l0.sku}` : ""}
              </div>
            </>
          ) : (
            <>
              <div className="font-medium text-slate-800 underline decoration-dotted decoration-slate-300 underline-offset-2 group-hover:text-[#002FA7]">{lines.length} Items</div>
              {shown.map((l, i) => (
                <p key={l.id || i} className="max-w-[220px] truncate text-xs text-slate-500">
                  <span className="font-semibold tabular-nums text-slate-700">{num(l.quantity)}</span> <span className="text-slate-400">{l.unit}</span> {l.name}
                </p>
              ))}
              {lines.length > 3 && <p className="text-[10px] font-semibold text-[#002FA7]">+{lines.length - 3} more…</p>}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto max-w-[min(680px,92vw)] overflow-x-auto rounded-sm p-0" onClick={(e) => e.stopPropagation()}>
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">Item</th><th className="px-3 py-2">SKU</th>
              <th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2">Unit</th>
              <th className="px-3 py-2 text-right">Unit Price</th><th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Received</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.id || i} className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-2 font-medium text-slate-800">{l.name}{l.edd ? <span className="ml-1 text-[9px] text-amber-600">EDD {fmtShort(l.edd)}</span> : null}</td>
                <td className="px-3 py-2 text-slate-500">{l.sku || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num(l.quantity)}</td>
                <td className="px-3 py-2 text-slate-500">{l.unit}</td>
                <td className="px-3 py-2 text-right tabular-nums">{l.unit_price > 0 ? money(l.unit_price, po.currency) : "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{l.line_total > 0 ? money(l.line_total, po.currency) : "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{num(l.received)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PopoverContent>
    </Popover>
  );
}

export function PoCostCell({ po }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="block text-left" onClick={(e) => e.stopPropagation()} data-testid={`po-cost-${po.id}`}>
          {po.total > 0 ? (
            <>
              <p className="whitespace-nowrap font-semibold tabular-nums text-slate-900 hover:text-[#002FA7]">{money(po.total, po.currency)}</p>
              {po.outstanding_value > 0 && po.outstanding_value < po.subtotal && <p className="whitespace-nowrap text-[10px] tabular-nums text-amber-600">{money(po.outstanding_value, po.currency)} open</p>}
            </>
          ) : <span className="text-xs text-slate-300 hover:text-[#002FA7]">No price</span>}
          {po.payment_terms && <p className="whitespace-nowrap text-[10px] text-slate-400">{po.payment_terms}</p>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 rounded-sm p-3 text-xs" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-1 tabular-nums">
          <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{money(po.subtotal, po.currency)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Freight</span><span>{money(po.freight_cost, po.currency)}</span></div>
          <div className="flex justify-between border-t border-slate-200 pt-1 font-bold"><span>Total</span><span>{money(po.total, po.currency)}</span></div>
          {po.outstanding_value > 0 && <div className="flex justify-between text-amber-700"><span>Outstanding value</span><span>{money(po.outstanding_value, po.currency)}</span></div>}
          {po.payment_terms && <p className="pt-1 text-slate-500">Terms: {po.payment_terms}</p>}
          {po.freight_terms && <p className="text-slate-500">Freight: {po.freight_terms}</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ConfirmQtyButton({ po, line, onChanged }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState("");
  const save = async () => {
    try {
      await api.put(`/purchase-orders/${po.id}/line-confirm`, { line_id: line.id, confirmed_qty: Number(v) || 0 });
      toast.success("Supplier confirmation recorded");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setV(String(line.confirmed_qty || line.quantity)); }}>
      <PopoverTrigger asChild>
        <button type="button" className={`tabular-nums hover:underline ${line.confirmed_qty > 0 ? "text-sky-700" : "text-slate-300"}`} onClick={(e) => e.stopPropagation()} data-testid={`po-confirm-btn-${po.id}-${line.id}`}>
          {line.confirmed_qty > 0 ? num(line.confirmed_qty) : "confirm"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 rounded-sm p-2" onClick={(e) => e.stopPropagation()}>
        <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">Qty confirmed by supplier</p>
        <div className="flex gap-1">
          <Input value={v} onChange={(e) => setV(e.target.value.replace(/[^0-9.]/g, ""))} className="h-8 rounded-sm text-right tabular-nums" data-testid={`po-confirm-input-${po.id}-${line.id}`} />
          <Button size="sm" className="h-8 rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" onClick={save} data-testid={`po-confirm-save-${po.id}-${line.id}`}>Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function PoFulfillmentCell({ po, onChanged }) {
  return (
    <div className="space-y-1" data-testid={`po-fulfillment-${po.id}`} onClick={(e) => e.stopPropagation()}>
      {(po.lines || []).map((l) => (
        <div key={l.id} className="whitespace-nowrap text-[11px] leading-tight">
          <span className="font-semibold tabular-nums text-slate-800">{num(l.quantity)}</span>
          <span className="text-slate-400"> ord · </span>
          <ConfirmQtyButton po={po} line={l} onChanged={onChanged} />
          <span className="text-slate-400"> conf · </span>
          <span className={`tabular-nums ${l.shipped_qty > 0 ? "text-amber-700" : "text-slate-300"}`}>{num(l.shipped_qty)}</span>
          <span className="text-slate-400"> shp</span>
          {l.outstanding_ship > 0 && po.status !== "cancelled" && po.status !== "draft" ? (
            <span className="ml-1 rounded-full bg-slate-100 px-1.5 text-[9px] font-semibold text-slate-500">{num(l.outstanding_ship)} awaiting</span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function PoShipmentsCell({ po, onChanged, onAddShipment, onEditShipment, onReceiveShipment }) {
  const ships = po.shipments || [];
  const canAdd = !["draft", "cancelled"].includes(po.status);
  const setStatus = async (s, v) => {
    try {
      await api.patch(`/inbound-shipments/${s.id}/status`, { status: v });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };
  if (!ships.length) {
    return canAdd ? (
      <button type="button" onClick={(e) => { e.stopPropagation(); onAddShipment(po); }}
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]"
        data-testid={`po-add-shipment-${po.id}`}><Truck className="h-3.5 w-3.5" /> Add Shipment</button>
    ) : <span className="text-xs text-slate-300">—</span>;
  }
  return (
    <div className="space-y-1" onClick={(e) => e.stopPropagation()} data-testid={`po-shipments-${po.id}`}>
      {ships.map((s) => (
        <div key={s.id} className="flex items-center gap-1 whitespace-nowrap">
          <Select value={s.status} onValueChange={(v) => setStatus(s, v)}>
            <SelectTrigger className="h-auto w-auto gap-1 rounded-full border-0 p-0 shadow-none" data-testid={`po-ship-status-${s.id}`}>
              <PoBadge meta={PO_SHIPMENT_META[s.status]} />
            </SelectTrigger>
            <SelectContent>{Object.entries(PO_SHIPMENT_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}</SelectContent>
          </Select>
          <button type="button" className="text-[10px] font-semibold text-[#002FA7] hover:underline" onClick={() => onEditShipment(po, s)} data-testid={`po-ship-edit-${s.id}`}>{s.shipment_number}</button>
          {s.status !== "delivered" && s.status !== "cancelled" && (
            <button type="button" title="Receive this shipment" className="text-slate-300 hover:text-emerald-600" onClick={() => onReceiveShipment(po, s)} data-testid={`po-ship-receive-${s.id}`}>
              <Plus className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
      {canAdd && (
        <button type="button" onClick={() => onAddShipment(po)} className="text-[10px] font-semibold text-slate-400 hover:text-[#002FA7]" data-testid={`po-add-shipment-${po.id}`}>+ shipment</button>
      )}
    </div>
  );
}

export function PoReceivingCell({ po, onReceive, onReverseReceipt }) {
  const pct = po.total_ordered > 0 ? Math.min(100, Math.round((po.total_received / po.total_ordered) * 100)) : 0;
  const meta = PO_RECEIVING_META[po.receiving_status] || PO_RECEIVING_META.not_received;
  const canReceive = ["open", "ready"].includes(po.status);
  const postedReceipts = (po.receipts_list || []).filter((r) => r.status !== "reversed");
  return (
    <div className="min-w-[130px] space-y-1" onClick={(e) => e.stopPropagation()} data-testid={`po-receiving-${po.id}`}>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="block w-full text-left">
            <div className="flex items-center justify-between gap-2">
              <PoBadge meta={meta} testId={`po-receiving-badge-${po.id}`} />
              <span className="text-[10px] tabular-nums text-slate-400">{pct}%</span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full rounded-full ${po.receiving_status === "discrepancy" ? "bg-red-400" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-0.5 whitespace-nowrap text-[10px] tabular-nums text-slate-400">{num(po.total_received)} / {num(po.total_ordered)} received</p>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 rounded-sm p-3 text-xs" onClick={(e) => e.stopPropagation()}>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Receiving — {po.po_number}</p>
          {(po.lines || []).map((l) => (
            <div key={l.id} className="mb-1.5">
              <p className="font-medium text-slate-800">{l.name}</p>
              <p className="tabular-nums text-slate-500">
                {num(l.received)} / {num(l.quantity)} accepted
                {l.damaged_qty > 0 && <span className="text-red-600"> · {num(l.damaged_qty)} damaged</span>}
                {l.rejected_qty > 0 && <span className="text-red-600"> · {num(l.rejected_qty)} rejected</span>}
                {l.remaining > 0 && <span className="text-amber-700"> · {num(l.remaining)} outstanding</span>}
                {l.closed && <span className="text-slate-400"> · closed short</span>}
              </p>
            </div>
          ))}
          {postedReceipts.length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">Receiving reports</p>
              {postedReceipts.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2">
                  <span className="text-slate-600">{r.receipt_number} · {(r.received_at || "").slice(0, 10)}{r.location ? ` · ${r.location}` : ""}</span>
                  <button type="button" className="text-[10px] font-semibold text-red-500 hover:underline" onClick={() => onReverseReceipt(po, r)} data-testid={`po-receipt-reverse-${r.id}`}>Reverse</button>
                </div>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>
      {canReceive && po.total_received < po.total_ordered && (
        <Button size="sm" variant="outline" className="h-6 w-full rounded-sm text-[10px]" onClick={() => onReceive(po, null)} data-testid={`po-receive-btn-${po.id}`}>
          Receive Shipment
        </Button>
      )}
    </div>
  );
}

export function PoTextCell({ po, field, onChanged, placeholder = "Set", testId, multiline = false }) {
  const [editing, setEditing] = useState(false);
  const value = po[field] || "";
  if (editing) {
    return multiline ? (
      <textarea autoFocus defaultValue={value} rows={3} className="w-44 rounded-sm border border-slate-300 p-1 text-xs"
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => { setEditing(false); if (e.target.value !== value) patchMeta(po, { [field]: e.target.value }, onChanged); }}
        data-testid={`${testId}-input`} />
    ) : (
      <input autoFocus defaultValue={value} className="w-36 rounded-sm border border-slate-300 px-1 py-0.5 text-xs"
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => { setEditing(false); if (e.target.value !== value) patchMeta(po, { [field]: e.target.value }, onChanged); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditing(false); }}
        data-testid={`${testId}-input`} />
    );
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`group block max-w-[170px] whitespace-pre-line text-left text-xs leading-tight hover:text-[#002FA7] ${value ? "text-slate-600" : "text-slate-300"}`}
      data-testid={testId}>
      {value || placeholder} <Pencil className="ml-0.5 inline h-2.5 w-2.5 opacity-0 group-hover:opacity-100" />
    </button>
  );
}

export function PoCarrierCell({ po, carriers, onChanged }) {
  return (
    <div onClick={(e) => e.stopPropagation()} className="space-y-0.5">
      <Select value={po.carrier || "none"} onValueChange={(v) => patchMeta(po, { carrier: v === "none" ? "" : v }, onChanged)}>
        <SelectTrigger className="h-7 w-auto max-w-[150px] gap-1 rounded-sm border-slate-200 px-2 text-xs" data-testid={`po-carrier-${po.id}`}>
          <span className={`truncate ${po.carrier ? "text-slate-700" : "text-slate-300"}`}>{po.carrier || "Set carrier"}</span>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No carrier</SelectItem>
          {[...new Set([...(carriers || []), ...(po.carrier ? [po.carrier] : [])])].filter(Boolean).map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
        </SelectContent>
      </Select>
      <PoTextCell po={po} field="tracking" onChanged={onChanged} placeholder="+ tracking #" testId={`po-tracking-${po.id}`} />
    </div>
  );
}

function docsUploader({ po, kind, onChanged }) {
  return async (files) => {
    const arr = [...(po[kind] || [])];
    for (const f of files) {
      const d = await uploadDocument(f);
      arr.push({ name: d.name || f.name, url: d.url, path: d.path });
    }
    await patchMeta(po, { [kind]: arr }, onChanged, `${files.length} file${files.length > 1 ? "s" : ""} attached`);
  };
}

export function PoDocsCell({ po, onChanged }) {
  const [busy, setBusy] = useState(false);
  const docs = po.docs || [];
  const onPick = async (e) => {
    const fs = Array.from(e.target.files || []);
    e.target.value = "";
    if (!fs.length) return;
    setBusy(true);
    try { await docsUploader({ po, kind: "docs", onChanged })(fs); } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  const remove = (i) => patchMeta(po, { docs: docs.filter((_, x) => x !== i) }, onChanged, "Document removed");
  return (
    <div onClick={(e) => e.stopPropagation()} data-testid={`po-docs-${po.id}`}>
      {docs.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline" data-testid={`po-docs-btn-${po.id}`}>
              <FileText className="h-3.5 w-3.5" /> {docs.length} doc{docs.length > 1 ? "s" : ""}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 rounded-sm p-2 text-xs" onClick={(e) => e.stopPropagation()}>
            {docs.map((d, i) => (
              <div key={i} className="flex items-center justify-between gap-2 py-0.5">
                <a href={d.url} target="_blank" rel="noreferrer" className="truncate text-[#002FA7] hover:underline">{d.name}</a>
                <button type="button" onClick={() => remove(i)} className="text-slate-300 hover:text-red-500" data-testid={`po-doc-remove-${po.id}-${i}`}><X className="h-3 w-3" /></button>
              </div>
            ))}
          </PopoverContent>
        </Popover>
      )}
      <label className={`${docs.length ? "mt-0.5" : ""} inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-sm border border-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]`} data-testid={`po-docs-add-${po.id}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />} Attach
        <input type="file" className="hidden" multiple onChange={onPick} />
      </label>
    </div>
  );
}

export function PoPhotosCell({ po, onChanged }) {
  const [busy, setBusy] = useState(false);
  const photos = po.photos || [];
  const onPick = async (e) => {
    const fs = Array.from(e.target.files || []);
    e.target.value = "";
    if (!fs.length) return;
    setBusy(true);
    try { await docsUploader({ po, kind: "photos", onChanged })(fs); } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  const remove = (i) => patchMeta(po, { photos: photos.filter((_, x) => x !== i) }, onChanged, "Photo removed");
  const addBtn = (
    <label className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-sm border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]" data-testid={`po-photos-add-${po.id}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Add Photos
      <input type="file" className="hidden" accept="image/*" multiple onChange={onPick} />
    </label>
  );
  if (!photos.length) return <div onClick={(e) => e.stopPropagation()}>{addBtn}</div>;
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-[#002FA7] hover:underline" data-testid={`po-photos-btn-${po.id}`}>
            <Camera className="h-3.5 w-3.5" /> {photos.length} photo{photos.length > 1 ? "s" : ""}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Load Photos — {po.po_number}</p>
          <div className="mb-2 grid grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <div key={i} className="group relative">
                <a href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={p.name} className="h-20 w-full rounded-sm border border-slate-200 object-cover" /></a>
                <button type="button" onClick={() => remove(i)} className="absolute -right-1.5 -top-1.5 hidden rounded-full bg-red-500 p-0.5 text-white group-hover:block" data-testid={`po-photo-remove-${po.id}-${i}`}><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
          {addBtn}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function PoPriorityCell({ po, onChanged }) {
  const p = po.priority || 3;
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Select value={String(p)} onValueChange={(v) => patchMeta(po, { priority: Number(v) }, onChanged)}>
        <SelectTrigger className={`h-6 w-auto gap-1 rounded-full border px-2 py-0 text-[10px] font-semibold ${PO_PRIORITY_META[p].cls}`} data-testid={`po-priority-${po.id}`}>
          <span>{PO_PRIORITY_META[p].short}</span>
        </SelectTrigger>
        <SelectContent>
          {[1, 2, 3, 4].map((k) => (<SelectItem key={k} value={String(k)}>{PO_PRIORITY_META[k].label}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function PoInvoicesCell({ po, onChanged, onAdd, onEdit }) {
  const invs = po.invoices || [];
  const setStatus = async (inv, status) => {
    try {
      await api.patch(`/po-invoices/${inv.id}/status`, { status });
      toast.success(`${inv.invoice_number} marked ${PO_INVOICE_META[status].label}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };
  const remove = async (inv) => {
    try {
      await api.delete(`/po-invoices/${inv.id}`);
      toast.success(`Invoice ${inv.invoice_number} removed`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };
  const addBtn = (
    <button type="button" onClick={() => onAdd(po)}
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm border border-dashed border-slate-300 px-2 py-0.5 text-[10px] font-semibold text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]"
      data-testid={`po-invoice-add-${po.id}`}>
      <Plus className="h-3 w-3" /> Invoice
    </button>
  );
  if (!invs.length) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <PoBadge meta={PO_INVOICE_META.not_invoiced} testId={`po-invoice-${po.id}`} />
        <div className="mt-1">{addBtn}</div>
      </div>
    );
  }
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="block text-left" data-testid={`po-invoice-${po.id}`}>
            <PoBadge meta={PO_INVOICE_META[po.invoice_status] || PO_INVOICE_META.not_invoiced} />
            <p className="mt-0.5 text-[10px] tabular-nums text-slate-500">
              {money(po.invoiced_total, po.currency)} invoiced{po.invoice_paid_total > 0 ? ` · ${money(po.invoice_paid_total, po.currency)} paid` : ""}
            </p>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[26rem] rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Supplier Invoices — {po.po_number}</p>
          <div className="space-y-2">
            {invs.map((inv) => (
              <div key={inv.id} className="rounded-sm border border-slate-200 p-2" data-testid={`po-invoice-row-${inv.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <button type="button" onClick={() => onEdit(po, inv)} className="truncate text-sm font-semibold text-[#002FA7] hover:underline" data-testid={`po-invoice-edit-${inv.id}`}>{inv.invoice_number}</button>
                  <span className="text-sm font-semibold tabular-nums text-slate-800">{money(inv.amount, inv.currency)}</span>
                </div>
                <p className="text-[11px] text-slate-500">
                  {inv.invoice_date ? `Inv ${fmtShort(inv.invoice_date)}` : "No date"}
                  {inv.due_date ? ` · Due ${fmtShort(inv.due_date)}` : ""}
                  {inv.payment_terms ? ` · ${inv.payment_terms}` : ""}
                  {inv.status === "partially_paid" && inv.paid_amount > 0 ? ` · ${money(inv.paid_amount, inv.currency)} paid` : ""}
                </p>
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Select value={inv.status} onValueChange={(v) => setStatus(inv, v)}>
                      <SelectTrigger className="h-6 w-auto gap-1 rounded-sm border-slate-200 px-1.5 text-[10px] font-semibold" data-testid={`po-invoice-status-${inv.id}`}>
                        <PoBadge meta={PO_INVOICE_META[inv.overdue ? "overdue" : inv.status]} />
                      </SelectTrigger>
                      <SelectContent>
                        {["unpaid", "partially_paid", "paid", "disputed"].map((s) => (
                          <SelectItem key={s} value={s} className="text-xs">{PO_INVOICE_META[s].label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(inv.docs || []).map((d, i) => (
                      <a key={i} href={d.url} target="_blank" rel="noreferrer" title={d.name} className="text-[#002FA7] hover:text-[#002080]" data-testid={`po-invoice-doc-${inv.id}-${i}`}>
                        <FileText className="h-3.5 w-3.5" />
                      </a>
                    ))}
                  </div>
                  <button type="button" onClick={() => remove(inv)} className="text-slate-300 hover:text-red-500" title="Remove invoice" data-testid={`po-invoice-del-${inv.id}`}><X className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between">
            {addBtn}
            <p className="text-[10px] tabular-nums text-slate-400">PO total {money(po.total, po.currency)}</p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function PoPickupDelivery({ po, onChanged }) {
  const v = po.ship_method || "unset";
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Select value={v} onValueChange={(nv) => patchMeta(po, { ship_method: nv === "unset" ? "" : nv }, onChanged)}>
        <SelectTrigger className="h-7 w-auto gap-1 rounded-sm border-slate-200 px-2 text-xs" data-testid={`po-pickup-delivery-${po.id}`}>
          <span className={v === "unset" ? "text-slate-300" : "text-slate-700"}>{v === "pickup" ? "We pick up" : v === "delivery" ? "Supplier delivers" : "Not set"}</span>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="unset">Not set</SelectItem>
          <SelectItem value="pickup">We pick up</SelectItem>
          <SelectItem value="delivery">Supplier delivers</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
